import { Card, CardContent, CardHeader, Stack } from '@mui/material';
import { Highlights, HighlightsProps } from '../../Highlights';
import {
  DimensionFilter,
  DimensionFilterProps,
} from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/DimensionFilter';
import { useState } from 'react';
import { ALL_OPTION } from '@features/campaigns/components/RealTimeOptimization/DimensionTabs';
import { DataGrid } from '@mui/x-data-grid';
import { columns, MinimalCpgDimension, OptimizationIndex } from './columns';
import { CpgDimension, CpgReport } from '../../../api/schema';
import { useFeatures } from '@utils/hooks';
import { significanceSx } from '@components/shared/significance-sx';
import { buildDimensionCounts } from '@features/reports';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { useCpgAIInsights } from '@features/ai-insights';

const STATISTICALLY_INSIGNIFICANT_CLASSNAME = 'statistically-insignificant';

type MinimalCpgReport = HighlightsProps['report'] & {
  dimensions: (MinimalCpgDimension & Pick<CpgDimension, 'dimensionName'>)[];
  aiInsights: Pick<CpgReport, 'aiInsights'>['aiInsights'];
};

type ComparisonByDimensionCardProps = {
  report: MinimalCpgReport;
  campaignName: string;
};

const isDimensionSlug = (
  value: string
): value is keyof typeof apiSlugToDimensionName => {
  return value in apiSlugToDimensionName;
};

export const ComparisonByDimensionCard = (
  props: ComparisonByDimensionCardProps
) => {
  const [activeTab, setActiveTab] = useState<string>(ALL_OPTION.value);
  const [optimizationIndex, setOptimizationIndex] = useState<OptimizationIndex>(
    'incrementalSalesPerImpressionIndex'
  );

  const { cpgAiInsights } = useCpgAIInsights(
    props.report,
    'summary',
    activeTab
  );

  const handleTabChange: DimensionFilterProps['onChange'] = (
    _,
    newValue: string
  ) => {
    setActiveTab(newValue);
  };
  const dimensionCounts = buildDimensionCounts(
    props.report.dimensions.map(d => ({ dimensionName: d.dimensionName }))
  );
  const filteredDimensions = props.report.dimensions.filter(
    d => activeTab === ALL_OPTION.value || d.dimensionName === activeTab
  );
  const features = useFeatures();
  const excludedColumns = new Set<ReturnType<typeof columns>[number]['field']>(
    features.cpgSalesRate ? [] : (['salesRate'] as const)
  );

  const aiInsightsTitle = isDimensionSlug(activeTab)
    ? apiSlugToDimensionName[activeTab]
    : 'All Dimensions';

  return (
    <Card>
      <CardHeader title="Comparison by Dimension" />
      <CardContent>
        <Stack direction="column" gap="1rem">
          <DimensionFilter
            dimensionCounts={dimensionCounts}
            allSubdimensionsCount={props.report.dimensions.length}
            activeTab={activeTab}
            onChange={handleTabChange}
          />

          <AIInsightsCard
            testId="cpg-comparison-by-dimension-ai-insights"
            aiInsights={cpgAiInsights}
            title={aiInsightsTitle}
            highlights={
              <Highlights
                report={props.report}
                campaignName={props.campaignName}
              />
            }
          />

          <DataGrid
            disableRowSelectionOnClick
            rows={filteredDimensions}
            columns={columns({
              optimizationIndex,
              setOptimizationIndex,
            }).filter(column => !excludedColumns.has(column.field))}
            pageSizeOptions={[5, 10, 25]}
            getRowClassName={params =>
              params.row.statisticallySignificant
                ? ''
                : STATISTICALLY_INSIGNIFICANT_CLASSNAME
            }
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            sx={{
              height: 'auto',
              padding: '1rem',
              ...significanceSx({
                insignificantClassname: STATISTICALLY_INSIGNIFICANT_CLASSNAME,
              }),
            }}
            data-testid="comparison-by-dimension-table"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
